// Shelf-pack a list of frames onto an atlas canvas and emit a TexturePacker
// JSON Hash describing each frame's packed location.

import { copyImageDataRect } from '~/lib/utils';
import type { Atlas, AtlasFrame } from '~/types/atlas';
import type { Frame } from '~/types/pipeline';

const PADDING = 1; // 1 px transparent padding to avoid bleed.
const MIN_ATLAS_W = 128;
const MAX_ATLAS_W = 4096;

export type PackLayout = {
  placements: Array<{ x: number; y: number; w: number; h: number }>;
  atlasW: number;
  atlasH: number;
};

export type PackResult = {
  canvas: OffscreenCanvas;
  atlas: Atlas;
};

function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

function isEmptyFrame(frame: Frame): boolean {
  return frame.trimRect.w === 0 && frame.trimRect.h === 0;
}

function chooseAtlasWidth(frames: readonly Frame[], padding: number, pow2: boolean): number {
  const totalArea = frames.reduce((acc, f) => {
    const w = f.trimRect.w + 2 * padding;
    const h = f.trimRect.h + 2 * padding;
    return acc + w * h;
  }, 0);
  const maxFrameW = frames.reduce((m, f) => Math.max(m, f.trimRect.w + 2 * padding), 0);
  let target = Math.max(MIN_ATLAS_W, maxFrameW, Math.ceil(Math.sqrt(totalArea * 1.2)));
  if (pow2) target = nextPow2(target);
  return Math.min(MAX_ATLAS_W, target);
}

// Shelf packer: sort frames by height desc; place left-to-right with padding
// gaps; new shelf when current row would overflow atlasW.
function shelfPackFrames(
  frames: readonly Frame[],
  atlasW: number,
  padding: number,
): { placements: PackLayout['placements']; atlasH: number } {
  const order = frames
    .map((_, i) => i)
    .sort((a, b) => {
      const fa = frames[a];
      const fb = frames[b];
      if (!fa || !fb) return 0;
      return fb.trimRect.h - fa.trimRect.h;
    });

  const placements: PackLayout['placements'] = new Array(frames.length);
  let cx = padding;
  let cy = padding;
  let shelfH = 0;

  for (const i of order) {
    const f = frames[i];
    if (!f) continue;
    if (isEmptyFrame(f)) {
      placements[i] = { x: 0, y: 0, w: 0, h: 0 };
      continue;
    }
    const fw = f.trimRect.w;
    const fh = f.trimRect.h;

    if (cx + fw + padding > atlasW) {
      cy += shelfH + padding;
      cx = padding;
      shelfH = 0;
    }

    placements[i] = { x: cx, y: cy, w: fw, h: fh };
    cx += fw + padding;
    if (fh > shelfH) shelfH = fh;
  }

  const atlasH = cy + shelfH + padding;
  return { placements, atlasH };
}

/** Compute pack layout (pure, no canvas). */
export function computePackLayout(
  frames: readonly Frame[],
  options: { padding?: number; pow2?: boolean } = {},
): PackLayout {
  const padding = options.padding ?? PADDING;
  const pow2 = options.pow2 ?? false;

  const atlasW = chooseAtlasWidth(frames, padding, pow2);
  const { placements, atlasH } = shelfPackFrames(frames, atlasW, padding);
  const finalH = pow2 ? nextPow2(atlasH) : atlasH;

  return { placements, atlasW, atlasH: finalH };
}

/** Derive a 4-digit zero-padded name from the frame index. */
function frameName(frame: Frame): string {
  return String(frame.index).padStart(4, '0');
}

/** Pack frames into an OffscreenCanvas atlas and emit TexturePacker JSON Hash. */
export function packAtlas(
  frames: readonly Frame[],
  source: ImageData,
  options: {
    padding?: number;
    pow2?: boolean;
    appName?: string;
    appVersion?: string;
    imageName?: string;
  } = {},
): PackResult {
  const {
    padding = PADDING,
    pow2 = false,
    appName = 'Spriteer',
    appVersion = '0.1.0',
    imageName = 'atlas.png',
  } = options;

  const { placements, atlasW, atlasH } = computePackLayout(frames, { padding, pow2 });

  const canvas = new OffscreenCanvas(atlasW, atlasH);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d context from OffscreenCanvas');
  ctx.imageSmoothingEnabled = false;

  const atlasFrames: Record<string, AtlasFrame> = {};

  for (let i = 0; i < frames.length; i++) {
    const f = frames[i];
    const p = placements[i];
    if (!f || !p) continue;
    if (isEmptyFrame(f)) continue;

    const trimData = copyImageDataRect(source, f.trimRect);
    ctx.putImageData(trimData, p.x, p.y);

    const trimmed =
      f.spriteSourceSize.x !== 0 ||
      f.spriteSourceSize.y !== 0 ||
      f.trimRect.w !== f.sourceSize.w ||
      f.trimRect.h !== f.sourceSize.h;

    atlasFrames[frameName(f)] = {
      frame: { x: p.x, y: p.y, w: p.w, h: p.h },
      rotated: false,
      trimmed,
      spriteSourceSize: {
        x: f.spriteSourceSize.x,
        y: f.spriteSourceSize.y,
        w: p.w,
        h: p.h,
      },
      sourceSize: { w: f.sourceSize.w, h: f.sourceSize.h },
      pivot: f.pivot,
    };
  }

  const atlas: Atlas = {
    frames: atlasFrames,
    meta: {
      app: appName,
      version: appVersion,
      image: imageName,
      format: 'RGBA8888',
      size: { w: atlasW, h: atlasH },
      scale: '1',
    },
  };

  return { canvas, atlas };
}
